import {
  Accordion,
  Alert,
  MobileAccordionSelection,
  AttributeHeader,
  AttributeValue,
  Button,
  CustomMobileAccordionType,
  Danger,
  DataTable,
  DataTableColumn,
  DataTableDataType,
  EditTableFormatter,
  Hint,
  Typography,
  urlReplace,
  useLoading,
  includes,
  SortFilters,
  ASC,
  tableSort,
  SortDirection,
  createCssVariableString,
  PriceFormatter,
} from "best-common-react";
import React, { CSSProperties, useCallback, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useMutation } from "@tanstack/react-query";
import RouteConstants from "../../constants/RouteConstants";
import {
  AdminPurchaseOpportunityOverviewDTO,
  AdminPurchaseOpportunityOverviewEntryDTO,
  AdminPurchaseOpportunityOverviewGameDTO,
  PurchaseOpportunityFilters,
  PurchaseOpportunityStatusId,
} from "../../types/PurchaseOpportunity";
import { disablePurchaseOpportunityGame } from "../../api/RequesTixApi";
import AdminPurchaseOpportunityBulkTransactions from "./AdminPurchaseOpportunityBulkTransactions";

type EntryData = DataTableDataType & {
  user: string;
  firstName: string;
  lastName: string;
  purchaseOpportunityEntryId: number;
  status: string;
  purchaseOpportunityId?: number;
  purchaseOpportunityStatusId: PurchaseOpportunityStatusId;
  ballparkEmailAddress: string;
  priority: number;
  userBillingId: number | null;
  lastChargedDate: Date | string | null;
  lastChargedAmount: number | null;
  lastRefundDate: Date | string | null;
  lastRefundAmount: number | null;
  actionsDisabled: boolean;
};

const PurchaseOppGameAccordion: React.FC<React.PropsWithChildren<CustomMobileAccordionType<EntryData>>> = ({
  data,
  index,
  ...rest
}) => {
  const navigate = useNavigate();

  const onEditClick = useCallback(() => {
    navigate(
      urlReplace(RouteConstants.PURCHASE_OPPORTUNITY.REQUEST_EDIT, {
        purchaseOpportunityId: data.purchaseOpportunityId,
        purchaseOpportunityEntryId: data.purchaseOpportunityEntryId,
      }),
    );
  }, [navigate, data.purchaseOpportunityId, data.purchaseOpportunityEntryId]);

  return (
    <Accordion {...rest}>
      <Accordion.Header>
        <div className="d-flex">
          <MobileAccordionSelection index={index} />
          <EditTableFormatter
            value={data.purchaseOpportunityId}
            row={data}
            onClick={onEditClick}
            validateFn={(entry) => !entry.actionsDisabled}
          />
          <Typography elementType="span" className="bcr-mx-2">
            {data.user}
          </Typography>
          <Typography elementType="span">{data.status}</Typography>
        </div>
      </Accordion.Header>
      <Accordion.Body>
        <div>
          <AttributeHeader>Priority</AttributeHeader>
          <AttributeValue>{data.priority}</AttributeValue>
        </div>
        <div>
          <AttributeHeader>Ballpark Email Address</AttributeHeader>
          <AttributeValue>{data.ballparkEmailAddress}</AttributeValue>
        </div>
      </Accordion.Body>
    </Accordion>
  );
};

const convertEntry = (
  entry: AdminPurchaseOpportunityOverviewEntryDTO,
  opp: AdminPurchaseOpportunityOverviewDTO,
  actionsDisabled: boolean,
): EntryData => ({
  id: entry.purchaseOpportunityEntryId,
  user: `${entry.firstName} ${entry.lastName}`,
  firstName: entry.firstName,
  lastName: entry.lastName,
  purchaseOpportunityId: opp.purchaseOpportunityId,
  purchaseOpportunityEntryId: entry.purchaseOpportunityEntryId,
  status: entry.purchaseOpportunityStatus.description,
  purchaseOpportunityStatusId: entry.purchaseOpportunityStatus.purchaseOpportunityStatusId,
  ballparkEmailAddress: entry.ballparkEmailAddress,
  priority: entry.priority,
  userBillingId: entry.userBillingId,
  lastChargedDate: entry.lastChargedDate,
  lastChargedAmount: entry.lastChargedAmount,
  lastRefundDate: entry.lastRefundDate,
  lastRefundAmount: entry.lastRefundAmount,
  actionsDisabled,
});

const filterEntries = (
  entries: AdminPurchaseOpportunityOverviewEntryDTO[],
  filters: PurchaseOpportunityFilters,
): AdminPurchaseOpportunityOverviewEntryDTO[] => {
  return entries.filter((entry: AdminPurchaseOpportunityOverviewEntryDTO) => {
    const matchesStatus =
      !filters.statusIds?.length ||
      filters.statusIds.includes(entry.purchaseOpportunityStatus.purchaseOpportunityStatusId);
    const freeText = filters.freeText?.trim().toLowerCase();
    const matchesText =
      !freeText ||
      includes(entry.firstName.toLowerCase(), freeText) ||
      includes(entry.lastName.toLowerCase(), freeText) ||
      includes(entry.ballparkEmailAddress.toLowerCase(), freeText);
    return matchesStatus && matchesText;
  });
};

type AdminPurchaseOpportunityGameProps = {
  opp: AdminPurchaseOpportunityOverviewDTO;
  game: AdminPurchaseOpportunityOverviewGameDTO;
  filters: PurchaseOpportunityFilters;
  reload: () => Promise<void>;
  actionsDisabled: boolean;
  onProcessingChange: (processing: boolean) => void;
};

const AdminPurchaseOpportunityGame: React.FC<AdminPurchaseOpportunityGameProps> = ({
  opp,
  game,
  filters,
  reload,
  actionsDisabled,
  onProcessingChange,
}) => {
  const navigate = useNavigate();
  const { setLoading } = useLoading();
  const [selectedEntryIds, setSelectedEntryIds] = useState<number[]>([]);
  const [sortFilters, setSortFilters] = useState<SortFilters<EntryData>>({
    key: "user",
    direction: ASC,
  });
  const [bulkType, setBulkType] = useState<"charge" | "refund" | undefined>(undefined);
  const [disableError, setDisableError] = useState<string>();
  const [disableSucceeded, setDisableSucceeded] = useState(false);
  const disableRunningRef = useRef(false);

  const disableGameMutation = useMutation({
    mutationFn: async (gameId: number) => {
      await disablePurchaseOpportunityGame(gameId);
    },
    retry: false,
  });

  const navigateToEntry = useCallback(
    (entryId: number) => {
      navigate(
        urlReplace(RouteConstants.PURCHASE_OPPORTUNITY.REQUEST_EDIT, {
          purchaseOpportunityId: opp.purchaseOpportunityId,
          purchaseOpportunityEntryId: entryId,
        }),
      );
    },
    [navigate, opp.purchaseOpportunityId],
  );

  const Columns: DataTableColumn<EntryData>[] = useMemo(
    () => [
      {
        name: "",
        key: "purchaseOpportunityEntryId",
        width: 40,
        onClick: (_rowIndex: number, row: EntryData) => {
          if (!actionsDisabled) {
            navigateToEntry(row.purchaseOpportunityEntryId);
          }
        },
        validateFn: () => !actionsDisabled,
        readonlyFormatter: EditTableFormatter,
        fixed: true,
      },
      {
        key: "user",
        name: "User",
        width: 300,
        sortable: true,
        fixed: true,
      },
      {
        key: "status",
        name: "Status",
        width: 150,
        sortable: true,
      },
      {
        key: "priority",
        name: "Priority",
        width: 75,
        sortable: true,
      },
      {
        key: "ballparkEmailAddress",
        name: "Ballpark Email Address",
        width: 250,
        sortable: true,
      },
      {
        key: "lastChargedAmount",
        name: "Last Charged Amount",
        width: 150,
        sortable: true,
        readonlyFormatter: PriceFormatter,
      },
      {
        key: "lastRefundAmount",
        name: "Last Refund Amount",
        width: 150,
        sortable: true,
        readonlyFormatter: PriceFormatter,
      },
    ],
    [actionsDisabled, navigateToEntry],
  );

  const rowStylesGetter = useCallback((_index: number, value: EntryData): CSSProperties | undefined => {
    switch (value.purchaseOpportunityStatusId) {
      case 2:
        return { background: createCssVariableString("coreColorsColoredSurfaceBlue") };
      case 3:
        return { background: createCssVariableString("coreColorsColoredSurfaceYellow") };
      case 4:
        return { background: createCssVariableString("coreColorsColoredSurfaceGreen") };
      case 5:
        return { background: createCssVariableString("coreColorsColoredSurfaceRed") };
      default:
        return {};
    }
  }, []);

  const disableGame = async () => {
    if (disableRunningRef.current || disableSucceeded) {
      return;
    }

    disableRunningRef.current = true;
    setDisableError(undefined);
    onProcessingChange(true);
    let releasePageActions = true;
    try {
      setLoading(true);
      await disableGameMutation.mutateAsync(game.purchaseOpportunityGameId);
      setDisableSucceeded(true);
      try {
        await reload();
      } catch {
        releasePageActions = false;
        setDisableError(
          "Game was disabled, but the opportunity overview could not be refreshed. Reload the page before taking another action.",
        );
      }
    } catch {
      setDisableError("Game could not be disabled. Please try again.");
    } finally {
      disableRunningRef.current = false;
      if (releasePageActions) {
        onProcessingChange(false);
      }
      setLoading(false);
    }
  };

  const rawData: AdminPurchaseOpportunityOverviewEntryDTO[] = useMemo(() => {
    return filterEntries(game.adminEntries, filters);
  }, [game.adminEntries, filters]);

  const data: EntryData[] = useMemo(() => {
    return rawData.map((entry: AdminPurchaseOpportunityOverviewEntryDTO) => convertEntry(entry, opp, actionsDisabled));
  }, [actionsDisabled, rawData, opp]);

  const displayData: EntryData[] = useMemo(() => {
    return tableSort([...data], sortFilters);
  }, [data, sortFilters]);

  const selectedOpps: AdminPurchaseOpportunityOverviewEntryDTO[] = useMemo(
    () => rawData.filter((entry) => selectedEntryIds.includes(entry.purchaseOpportunityEntryId)),
    [rawData, selectedEntryIds],
  );

  const selectedCount: number = useMemo(
    () => data.filter((entry) => [2, 4].includes(entry.purchaseOpportunityStatusId)).length,
    [data],
  );
  const notSelectedCount: number = useMemo(
    () => data.filter((entry) => 3 === entry.purchaseOpportunityStatusId).length,
    [data],
  );
  const chargedCount: number = useMemo(
    () => data.filter((entry) => 4 === entry.purchaseOpportunityStatusId).length,
    [data],
  );
  const refundedCount: number = useMemo(
    () => data.filter((entry) => 5 === entry.purchaseOpportunityStatusId).length,
    [data],
  );

  const isSelectedReadyForCharge = useMemo(
    () =>
      selectedOpps.some(
        (entry: AdminPurchaseOpportunityOverviewEntryDTO) =>
          entry.purchaseOpportunityStatus.purchaseOpportunityStatusId === 2,
      ),
    [selectedOpps],
  );
  const isSelectedReadyForRefund = useMemo(
    () =>
      selectedOpps.some(
        (entry: AdminPurchaseOpportunityOverviewEntryDTO) =>
          entry.purchaseOpportunityStatus.purchaseOpportunityStatusId === 4,
      ),
    [selectedOpps],
  );

  const canBulkCharge = useMemo(
    () =>
      selectedOpps.length > 0 &&
      selectedOpps.every((entry) => entry.purchaseOpportunityStatus.purchaseOpportunityStatusId === 2),
    [selectedOpps],
  );

  const canBulkRefund = useMemo(
    () =>
      selectedOpps.length > 0 &&
      selectedOpps.every((entry) => entry.purchaseOpportunityStatus.purchaseOpportunityStatusId === 4),
    [selectedOpps],
  );

  const isSelected = useCallback(
    (rowIndex: number) => selectedEntryIds.includes(displayData[rowIndex].purchaseOpportunityEntryId),
    [selectedEntryIds, displayData],
  );

  const onSelectionChange = useCallback(
    (rowIndexes: number[], selected: boolean) => {
      const itemIds: number[] = rowIndexes.map((index) => displayData[index].purchaseOpportunityEntryId);
      setSelectedEntryIds((currentIds) =>
        selected
          ? Array.from(new Set([...currentIds, ...itemIds]))
          : currentIds.filter((entryId) => !itemIds.includes(entryId)),
      );
    },
    [displayData],
  );

  const isSelectionDisabled = useCallback(
    (rowIndex: number) => {
      const statusId: PurchaseOpportunityStatusId | undefined = displayData[rowIndex]?.purchaseOpportunityStatusId;
      if (actionsDisabled || displayData[rowIndex]?.userBillingId === null || [1, 3, 5].includes(statusId)) {
        return true;
      } else if (isSelectedReadyForCharge) {
        return statusId !== 2;
      } else if (isSelectedReadyForRefund) {
        return statusId !== 4;
      }
      return false;
    },
    [actionsDisabled, displayData, isSelectedReadyForCharge, isSelectedReadyForRefund],
  );

  return (
    <>
      <AdminPurchaseOpportunityBulkTransactions
        isOpen={!!bulkType}
        close={() => {
          setBulkType(undefined);
          setSelectedEntryIds([]);
        }}
        isCharge={bulkType === "charge"}
        amount={opp.ticketCost}
        purchaseOpportunities={selectedOpps}
        reload={reload}
        onProcessingChange={onProcessingChange}
      />
      <Accordion accordionKey={`game-${game.gameNumber}`} startOpen animation={false}>
        <Accordion.Header>
          <Typography elementType="div" className="d-flex flex-row flex-wrap align-items-center bcr-gap-2">
            <b>Home Game {game.gameNumber}</b> {!!game?.gamePk ? `Game PK - ${game.gamePk}` : ""}
            <Hint>Total Entries: {data.length}</Hint>
            <Hint>Selected Entries: {selectedCount}</Hint>
            <Hint>Not Selected Entries: {notSelectedCount}</Hint>
            <Hint>Charged Entries: {chargedCount}</Hint>
            <Hint>Refunded Entries: {refundedCount}</Hint>
            {!!game.disabled && <Danger>Disabled</Danger>}
          </Typography>
        </Accordion.Header>
        <Accordion.Body>
          <div className="d-flex flex-column w-100">
            {!!disableError && (
              <Alert type="danger" className="bcr-mb-2">
                {disableError}
              </Alert>
            )}
            <div className="d-flex flex-row bcr-mb-2">
              <Button
                variant="default"
                className="bcr-me-2"
                disabled={game.disabled || disableSucceeded || actionsDisabled}
                onClick={() => void disableGame()}
              >
                Disable Game
              </Button>
              <Button
                variant="secondary"
                className="bcr-me-2"
                disabled={!canBulkCharge || actionsDisabled}
                onClick={() => setBulkType("charge")}
              >
                Bulk Charge
              </Button>
              <Button
                variant="secondary"
                className="bcr-me-2"
                disabled={!canBulkRefund || actionsDisabled}
                onClick={() => setBulkType("refund")}
              >
                Bulk Refund
              </Button>
            </div>
            <div style={{ height: 500 }}>
              <DataTable
                columns={Columns}
                data={displayData}
                statusTextOverride={(selectedCount: number, totalCount: number) =>
                  selectedCount > 0
                    ? selectedCount === totalCount
                      ? `${selectedCount} entries selected`
                      : `${selectedCount} selected of ${totalCount} entries`
                    : `${totalCount} entries`
                }
                rowStylesGetter={rowStylesGetter}
                accordion={PurchaseOppGameAccordion}
                tableHeights={{
                  maxHeightOffset: 0,
                  maxTableHeight: 400,
                }}
                sortColumn={sortFilters.key}
                sortDirection={sortFilters.direction}
                sortFunction={(key: keyof EntryData, direction: SortDirection) => {
                  setSortFilters({ key, direction });
                }}
                rowSelection={{
                  isSelected,
                  onSelectionChange,
                  isSelectionDisabled,
                }}
              />
            </div>
          </div>
        </Accordion.Body>
      </Accordion>
    </>
  );
};

export default AdminPurchaseOpportunityGame;
